import PageHeader from "@/components/PageHeader";
import { BRAND } from "@/data/content";

const CONTENT = {
    privacy: {
        eyebrow: "Legal",
        title: "Privacy Policy",
        subtitle: "How we handle your data. Plain English, updated November 2025.",
        body: [
            ["What we collect", `We collect the email address you provide when subscribing to our newsletter, downloading an ebook, or contacting us. We also collect standard anonymous analytics (page views, referrers) via Google Analytics.`],
            ["How we use it", `Your email is used only to deliver the content you signed up for, plus our weekly newsletter. You can unsubscribe with one click at any time.`],
            ["Third parties", `We use standard third-party providers (Google Analytics, our email tool, and the hosting infrastructure). None of them receive your data for anything beyond providing their service to us.`],
            ["Your rights", `You can request a copy or deletion of your data any time by emailing ${BRAND.email} or via the /contact form on the site.`],
            ["Cookies", `We use minimal cookies for analytics. You can disable them in your browser without breaking the site.`],
            ["Contact", `Questions about this policy? Reach out via /contact.`],
        ],
    },
    "affiliate-disclosure": {
        eyebrow: "Legal",
        title: "Partners & Referral Disclosure",
        subtitle: "How we keep the lights on — no surprises.",
        body: [
            ["The short version", `Some links on this website are partner links. This means we may earn a small referral reward if you purchase through our links, at no extra cost to you. We only recommend products and services that we believe provide value to our readers.`],
            ["Editorial independence", `Partner relationships never buy a positive review. Products are ranked on merit, tested first, and disclosed clearly. If a product doesn't earn its place, we don't cover it.`],
            ["Which links are partner links?", `Any external product link inside the "Picks" or "AI Tools" sections may be a partner link. We tag them with rel="sponsored" where applicable.`],
            ["Questions", `If any recommendation ever feels off, tell us via /contact and we'll investigate.`],
        ],
    },
    disclaimer: {
        eyebrow: "Legal",
        title: "Disclaimer",
        subtitle: "Business results depend on you. Read this before you start.",
        body: [
            ["Not financial advice", `Content on ${BRAND.name} is for education and inspiration. It is not personalised financial, tax, or legal advice.`],
            ["Earnings", `Any earnings mentioned are our own or verified third-party case studies. Your results will vary and are dependent on many factors — including effort, timing, and luck.`],
            ["Accuracy", `We do our best to keep content current, but the internet moves fast. If you spot an error, please tell us.`],
            ["No guarantees", `Nothing on this site guarantees any outcome, income, or result.`],
        ],
    },
    terms: {
        eyebrow: "Legal",
        title: "Terms & Conditions",
        subtitle: "The ground rules for using this site.",
        body: [
            ["Acceptance", `By using ${BRAND.name}, you agree to these terms. If you don't, please close the tab.`],
            ["Use of content", `All content is © ${BRAND.name}. You may quote up to 200 words with attribution and a link back. Full republication requires written permission.`],
            ["Third-party links", `We link out to many third-party tools and services. We are not responsible for their content, availability, or their terms.`],
            ["Warranty", `Everything is provided "as is". We disclaim liability to the extent permitted by law.`],
            ["Changes", `We may update these terms occasionally. Continued use of the site after an update means you accept the change.`],
            ["Contact", `Reach out to ${BRAND.email} or via the /contact form for any questions.`],
        ],
    },
};

export default function LegalPage({ pageKey = "privacy" }) {
    const c = CONTENT[pageKey] || CONTENT.privacy;
    return (
        <>
            <PageHeader eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} />
            <section className="container-editorial pb-24 max-w-3xl">
                <div className="space-y-10">
                    {c.body.map(([h, p], i) => (
                        <div key={i} data-testid={`legal-${pageKey}-section-${i}`}>
                            <h2 className="font-serif text-2xl">{h}</h2>
                            <p className="text-stone-700 mt-3 leading-relaxed">{p}</p>
                        </div>
                    ))}
                </div>
                <p className="mt-16 text-sm text-stone-500 border-t border-stone-200 pt-6">
                    Last updated: November 2025.
                </p>
            </section>
        </>
    );
}
